import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Instagram, Youtube, ArrowRight, Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { LAUNCH_TS, LAUNCH_LABEL } from "@/launch";

const pad2 = (n) => String(n).padStart(2, "0");

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = "https://images.unsplash.com/photo-1573152958734-1922c188fba3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9vZHklMjBjdWx0dXJlJTIwY3Jvd2QlMjBjb25jZXJ0fGVufDB8fHx8MTc4NDYwMTg1OXww&ixlib=rb-4.1.0&q=85";
const NETWORKS = ["VLMAG", "VLMAG MUSIC", "VLMAG STYLE"];
const SOCIALS = [
  { icon: Instagram, label: "@vlmagonline", id: "vlmagonline", href: "https://www.instagram.com/vlmagonline" },
  { icon: Instagram, label: "@vlmagmusic", id: "vlmagmusic", href: "https://www.instagram.com/vlmagmusic" },
  { icon: Instagram, label: "@vlmagstyle", id: "vlmagstyle", href: "https://www.instagram.com/vlmagstyle" },
  { icon: Youtube, label: "YouTube", id: "youtube", href: "https://www.youtube.com/@VLMAGONLINE" },
];

const LINES = ["LAUNCHING", "SOON."];

function timeAgo(iso) {
  if (!iso) return "just now";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ComingSoon({ onLive }) {
  const [form, setForm] = useState({ email: "", name: "", network: "", city: "" });
  const [count, setCount] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tickIdx, setTickIdx] = useState(0);
  const [state, setState] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(() => LAUNCH_TS - Date.now());

  const loadStats = () =>
    axios.get(`${API}/waitlist/stats`).then((r) => {
      setCount(r.data.display_count);
      setRecent(r.data.recent || []);
    }).catch(() => {});

  useEffect(() => { loadStats(); }, []);

  // launch countdown
  useEffect(() => {
    const t = setInterval(() => {
      const r = LAUNCH_TS - Date.now();
      setRemaining(r);
      if (r <= 0) {
        clearInterval(t);
        if (onLive) onLive();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [onLive]);

  const live = remaining <= 0;
  const days = Math.max(0, Math.floor(remaining / 86400000));
  const hours = Math.max(0, Math.floor((remaining % 86400000) / 3600000));
  const mins = Math.max(0, Math.floor((remaining % 3600000) / 60000));
  const secs = Math.max(0, Math.floor((remaining % 60000) / 1000));
  const COUNTDOWN = [
    { label: "Days", value: days, key: "days" },
    { label: "Hrs", value: hours, key: "hours" },
    { label: "Min", value: mins, key: "minutes" },
    { label: "Sec", value: secs, key: "seconds" },
  ];

  // rotate the recent-activity ticker
  useEffect(() => {
    if (recent.length < 1) return;
    const t = setInterval(() => setTickIdx((i) => (i + 1) % recent.length), 3500);
    return () => clearInterval(t);
  }, [recent]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email) { setError("Enter your email to join."); return; }
    setState("sending");
    try {
      const r = await axios.post(`${API}/waitlist`, form);
      setState("done");
      loadStats();
      void r;
    } catch (err) {
      setState("idle");
      setError(err?.response?.data?.detail?.[0]?.msg || "That email doesn't look right. Try again.");
    }
  };

  return (
    <div data-testid="coming-soon-page" className="relative min-h-screen bg-black text-white overflow-x-hidden flex flex-col">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="VLMAG" className="w-full h-full object-cover grayscale-img opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />
      </div>

      {/* Top logo */}
      <div className="relative z-10 px-5 md:px-10 pt-7">
        <div data-testid="cs-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
          <span className="text-[#E30613]">VL</span><span className="text-white">MAG</span>
        </div>
      </div>

      {/* Center */}
      <div className="relative z-10 flex-1 flex items-start md:items-center px-5 md:px-10 py-16">
        <div className="w-full max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-xs uppercase tracking-[0.3em] font-bold text-[#E30613] mb-6"
          >
            The Central Media Network — Fashion · Music · Art · Culture
          </motion.p>

          <h1 className="font-display uppercase leading-[0.82] tracking-tight text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 }}
                  className={`block ${i === 1 ? "text-[#E30613]" : "text-white"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="font-serif-ed italic text-xl md:text-2xl text-neutral-300 mt-8 max-w-xl"
          >
            We're building the media network telling the stories of the creatives, artists and entrepreneurs shaping the next generation of culture. Be first through the door.
          </motion.p>

          {/* Countdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.58 }}
            className="mt-10"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-400 mb-4">
              {live ? "The doors are open" : `Goes live · ${LAUNCH_LABEL}`}
            </p>
            {live ? (
              <a
                href="/preview"
                data-testid="launch-enter"
                className="group inline-flex items-center gap-3 bg-[#E30613] px-8 h-16 text-sm uppercase tracking-[0.25em] font-bold"
              >
                Enter VLMAG <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            ) : (
              <div className="flex gap-3 sm:gap-4" data-testid="launch-countdown">
                {COUNTDOWN.map((c) => (
                  <div key={c.key} className="flex flex-col items-center">
                    <div className="min-w-[68px] sm:min-w-[88px] border border-white/15 bg-black/50 backdrop-blur-sm px-2 py-3 sm:py-4">
                      <span
                        data-testid={`countdown-${c.key}`}
                        className="block font-display text-4xl sm:text-6xl leading-none text-white tabular-nums text-center"
                      >
                        {c.key === "days" ? c.value : pad2(c.value)}
                      </span>
                    </div>
                    <span className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">{c.label}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Form / success */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-10 max-w-xl"
          >
            {state === "done" ? (
              <div data-testid="waitlist-success" className="border border-[#E30613] bg-[#E30613]/10 p-6">
                <div className="flex items-center gap-3">
                  <Check className="text-[#E30613]" size={24} />
                  <p className="font-display uppercase text-2xl tracking-tight">You're on the list.</p>
                </div>
                <p className="text-neutral-300 mt-2 text-sm">We'll email you the moment VLMAG goes live. Follow us below so you don't miss the drop.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="waitlist-form" className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    data-testid="waitlist-name" value={form.name} onChange={set("name")}
                    placeholder="Your name (optional)"
                    className="h-14 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-500 text-base"
                  />
                  <Input
                    data-testid="waitlist-email" type="email" value={form.email} onChange={set("email")}
                    placeholder="you@email.com"
                    className="h-14 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-500 text-base"
                  />
                </div>

                <Input
                  data-testid="waitlist-city" value={form.city} onChange={set("city")}
                  placeholder="Your city (optional)"
                  className="h-14 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-500 text-base"
                />

                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Most excited for (optional)</p>
                  <div className="flex flex-wrap gap-2" data-testid="waitlist-networks">
                    {NETWORKS.map((n) => (
                      <button
                        type="button" key={n}
                        data-testid={`waitlist-network-${n.replace(/\s/g, "-").toLowerCase()}`}
                        onClick={() => setForm((f) => ({ ...f, network: f.network === n ? "" : n }))}
                        className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors duration-300 ${
                          form.network === n ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:border-white/60 hover:text-white"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {error && <p data-testid="waitlist-error" className="text-[#E30613] text-sm">{error}</p>}

                <button
                  type="submit" data-testid="waitlist-submit" disabled={state === "sending"}
                  className="group relative overflow-hidden w-full sm:w-auto bg-[#E30613] px-8 h-14 text-xs uppercase tracking-[0.25em] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {state === "sending" ? <Loader2 className="animate-spin" size={18} /> : <>Notify Me At Launch <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </form>
            )}

            {/* Counter + live activity */}
            <div className="mt-6 space-y-3">
              <p data-testid="waitlist-count" className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-400">
                {count === null ? "" : count > 0
                  ? <><span className="text-white">{count.toLocaleString()}</span> {count === 1 ? "creative" : "creatives"} already on the list</>
                  : "Be the first to join the list"}
              </p>
              {recent.length > 0 && (
                <div className="h-5 overflow-hidden" data-testid="waitlist-ticker">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={tickIdx}
                      initial={{ y: 14, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -14, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="flex items-center gap-2.5 text-xs text-neutral-400"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E30613] opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E30613]" />
                      </span>
                      <span>
                        {recent[tickIdx].city
                          ? <>Someone in <span className="text-white font-bold">{recent[tickIdx].city}</span> just joined <span className="text-white font-bold">{recent[tickIdx].network}</span></>
                          : <>A creative just joined <span className="text-white font-bold">{recent[tickIdx].network}</span></>}
                        <span className="text-neutral-600"> · {timeAgo(recent[tickIdx].created_at)}</span>
                      </span>
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>

          {/* Socials */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12 flex flex-wrap gap-3" data-testid="cs-socials"
          >
            {SOCIALS.map((s) => (
              <a
                key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                data-testid={`social-${s.id}`}
                className="group inline-flex items-center gap-2.5 border border-white/20 px-4 h-11 text-neutral-400 hover:bg-[#E30613] hover:border-[#E30613] hover:text-white transition-colors duration-300"
              >
                <s.icon size={17} />
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold">{s.label}</span>
              </a>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 px-5 md:px-10 pb-8">
        <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">© {new Date().getFullYear()} VLMAG. Vision. Culture. Progress.</p>
      </div>
    </div>
  );
}
